import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

interface ScrollRestorationProps {
  isLoading?: boolean;
}

export default function ScrollRestoration({ isLoading = false }: ScrollRestorationProps) {
  const location = useLocation();
  const itemsCache = useRef<NodeListOf<Element> | null>(null);
  // Hàm lưu vị trí scroll
  const saveScrollPosition = () => {
    // console.log("ScrollRestoration: User scrolled, saving position immediately");
    //
    let items = itemsCache.current;
    if (!items) {
      items = document.querySelectorAll('[data-item-index]');
      itemsCache.current = items;
    }
    
    // console.log("ScrollRestoration: Total items found:", items.length);
    
    if (items.length === 0) {
      // console.log("ScrollRestoration: No items found with data-item-index");
      return;
    }

    // Tìm item gần nhất
    const viewportHeight = window.innerHeight;
    const scrollTop = window.scrollY;
    const viewportCenter = scrollTop + viewportHeight / 2;
    
    let closestItemIndex = 0;
    let minDistance = Infinity;

    //
    for (let i = 0; i < items.length; i++) {
      const item = items[i] as HTMLElement;
      const rect = item.getBoundingClientRect();
      
      // 
      const itemTop = rect.top + scrollTop;
      const itemCenter = itemTop + rect.height / 2;
      
      //
      const distance = Math.abs(itemCenter - viewportCenter);
      
      if (distance < minDistance) {
        minDistance = distance;
        closestItemIndex = i;
      }
    }

    // console.log("ScrollRestoration: Selected item index:", closestItemIndex, "with distance:", minDistance);
    // console.log("ScrollRestoration: Viewport center:", viewportCenter);
    
    //
    const scrollData = {
      itemIndex: closestItemIndex,
      timestamp: Date.now()
    };
    
    // Lưu vào sessionStorage
    try {
      sessionStorage.setItem(
        `scroll-item-${location.pathname}`,
        JSON.stringify(scrollData)
      );
      
      // console.log("ScrollRestoration: Saved scroll data to sessionStorage:", scrollData);
      // console.log("ScrollRestoration: SessionStorage key:", `scroll-item-${location.pathname}`);
      
      // 
      // console.log("ScrollRestoration: Current sessionStorage content:");
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key) {
          const value = sessionStorage.getItem(key);
          // console.log(`ScrollRestoration: ${key} = ${value}`);
        }
      }
    } catch (error) {
      // console.error("ScrollRestoration: Error saving to sessionStorage:", error);
    }
  };

  // Tìm index của item 
  const findCurrentVisibleItemIndex = (): number => {
    const items = document.querySelectorAll('[data-item-index]');
    // console.log("ScrollRestoration: Total items found:", items.length);
    
    if (items.length === 0) {
      // console.log("ScrollRestoration: No items found with data-item-index");
      return -1;
    }

    const viewportHeight = window.innerHeight;
    const scrollTop = window.scrollY;
    const viewportCenter = scrollTop + viewportHeight / 2;
    
    // console.log("ScrollRestoration: Viewport center position:", viewportCenter);
    // console.log("ScrollRestoration: Current scroll position:", scrollTop);

    let closestItemIndex = 0;
    let minDistance = Infinity;

    // 
    for (let i = 0; i < items.length; i++) {
      const item = items[i] as HTMLElement;
      const rect = item.getBoundingClientRect();
      
      // 
      const itemTop = rect.top + scrollTop;
      const itemCenter = itemTop + rect.height / 2;
      
      // 
      const distance = Math.abs(itemCenter - viewportCenter);
      
      if (distance < minDistance) {
        minDistance = distance;
        closestItemIndex = i;
      }
    }

    // console.log("ScrollRestoration: Selected item index:", closestItemIndex, "with distance:", minDistance);
    return closestItemIndex;
  };

  useEffect(() => {
    // console.log("ScrollRestoration: Location changed to:", location.pathname);
    // console.log("ScrollRestoration: isLoading =", isLoading);
    
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
      // console.log("ScrollRestoration: Set scrollRestoration to manual");
    }

    //
    itemsCache.current = null;

    // Khôi phục vị trí scroll sau khi component đã render xong
    const restoreScrollPosition = () => {
      const savedScrollData = sessionStorage.getItem(`scroll-item-${location.pathname}`);
      // console.log("ScrollRestoration: Trying to restore scroll position for:", location.pathname);
      // console.log("ScrollRestoration: Saved scroll data from sessionStorage:", savedScrollData);
      
      if (savedScrollData) {
        try {
          const scrollData = JSON.parse(savedScrollData);
          // console.log("ScrollRestoration: Parsed scroll data:", scrollData);
          
          if (scrollData.itemIndex !== undefined) {
            const itemIndex = scrollData.itemIndex;
            // console.log("ScrollRestoration: Restoring to item index:", itemIndex);
            
            setTimeout(() => {
              const items = document.querySelectorAll('[data-item-index]');
              // console.log("ScrollRestoration: Found items:", items.length);
              
              if (items.length > itemIndex) {
                const targetItem = items[itemIndex];
                targetItem.scrollIntoView({ 
                  behavior: 'smooth', 
                  block: 'center' 
                });
                // console.log("ScrollRestoration: Scrolled to item index:", itemIndex);
              } else {
                // console.log("ScrollRestoration: Item index not found:", itemIndex);
                
                 window.scrollTo(0, 0);
                //  console.log("ScrollRestoration: Fallback to top of page");
              }
            }, 100);
          }
        } catch (error) {
          // console.error("ScrollRestoration: Error parsing scroll data:", error);
        }
      } else {
        // console.log("ScrollRestoration: No saved scroll data found for:", location.pathname);
      }
    };

    restoreScrollPosition();
    const timer = setTimeout(restoreScrollPosition, 500);

    return () => {
      
      const currentItemIndex = findCurrentVisibleItemIndex();
      // console.log("ScrollRestoration: Current visible item index:", currentItemIndex);
      
      clearTimeout(timer);
      
             if (currentItemIndex !== -1) {
         const scrollData = {
           itemIndex: currentItemIndex,
           timestamp: Date.now()
         };
        
        sessionStorage.setItem(
          `scroll-item-${location.pathname}`,
          JSON.stringify(scrollData)
        );
        // console.log("ScrollRestoration: Saved scroll data to sessionStorage:", scrollData);
      }
    };
  }, [location.pathname]);

  // Khôi phục vị trí scroll khi loading hoàn thành
  useEffect(() => {
    // console.log("ScrollRestoration: Loading state changed, isLoading =", isLoading);
    
    if (!isLoading) {
      // console.log("ScrollRestoration: Loading completed, attempting to restore scroll position");
      const savedScrollData = sessionStorage.getItem(`scroll-item-${location.pathname}`);
      // console.log("ScrollRestoration: Saved scroll data after loading:", savedScrollData);
      
      if (savedScrollData) {
        try {
          const scrollData = JSON.parse(savedScrollData);
          // console.log("ScrollRestoration: Parsed scroll data after loading:", scrollData);
          
          if (scrollData.itemIndex !== undefined) {
            const itemIndex = scrollData.itemIndex;
            // console.log("ScrollRestoration: Restoring item index after loading:", itemIndex);
            
            setTimeout(() => {
              const items = document.querySelectorAll('[data-item-index]');
              // console.log("ScrollRestoration: Found items after loading:", items.length);
              
              if (items.length > itemIndex) {
                const targetItem = items[itemIndex];
                targetItem.scrollIntoView({ 
                  behavior: 'smooth', 
                  block: 'center' 
                });
                // console.log("ScrollRestoration: Scrolled to item index after loading:", itemIndex);
              } else {
                // console.log("ScrollRestoration: Item index not found after loading:", itemIndex);
                
                 window.scrollTo(0, 0);
                //  console.log("ScrollRestoration: Fallback to top of page after loading");
              }
            }, 200);
          }
        } catch (error) {
          // console.error("ScrollRestoration: Error parsing scroll data after loading:", error);
        }
      }
    }
  }, [isLoading, location.pathname]);

  // 
  useEffect(() => {
    const handleScroll = () => {
      saveScrollPosition();
    };

    const handleBeforeUnload = () => {
      // console.log("ScrollRestoration: Page unloading, saving scroll position");
      saveScrollPosition();
    };

    window.addEventListener('scroll', handleScroll, { passive: false, capture: true });
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      window.removeEventListener('scroll', handleScroll, { capture: true });
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [location.pathname]);

  return null;
}
